import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  HStack,
  Text,
  Image,
  Center,
  Divider,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  VStack,
  useDisclosure,
  Hide,
  useBreakpointValue,
  Stack,
  Flex,
  Button as CButton,
  Show,
  Drawer,
  DrawerOverlay,
  DrawerBody,
  SlideFade,
  DrawerContent,
} from '@chakra-ui/react';

import { useLocation, useHistory } from 'react-router-dom';
import Cloud from '../../../assets/Cloud';
import logo from '../../../assets/logo-small.svg';
import { isSpruceAdmin, isAdmin, isCompanyUser, isDynamicLayoutAdmin } from './../../../utils/roles';
import LogoutLinkContainer from '../../login/LogoutLink/LogoutLinkContainer';
import MenuButton from '../MenuButton';
import PopoverLink from '../PopoverLink';
import { Container } from 'reactstrap';
import { MdSettings, MdMenu, MdClose } from 'react-icons/md';
import { AiFillCaretDown } from 'react-icons/ai';
import { includes } from 'lodash';

const AuthenticatedLayout = ({ userRole, userRoles }) => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const { isOpen: isOpenDrawer, onOpen: onOpenDrawer, onClose: onCloseDrawer } = useDisclosure();
  const isSmall = useBreakpointValue({ base: true, md: false });
  const ref = useRef();

  const { pathname } = useLocation();
  let history = useHistory();

  const redirect = useCallback(
    (path, isPopover = false) => {
      history.push(path);
      if (isPopover) {
        onClose();
        onCloseDrawer();
      }
    },
    [onClose, history],
  );

  return (
    <Container bgColor="#fff" pb={4}>
      <Stack
        direction={{ base: 'column', lg: 'row' }}
        justifyContent={'space-between'}
        pt={10}
        alignItems={'center'}
        w={'100%'}
      >
        <Flex w={{ base: '100%', md: 'unset' }} alignItems={'center'} ref={ref}>
          <Hide below="md">
            <Center height="44px">
              <Image src={logo} alt={'logo'} />
            </Center>
            <Center height="44px" mx={'20px'}>
              <Divider orientation="vertical" borderColor={'#425570'} />
            </Center>
            <Box mr={'10px'}>
              <Cloud fill={'#AA1F2E'} width={'40px'} height={'40px'} />
            </Box>
          </Hide>
          <HStack w={'100%'}>
            <Hide above="md">
              <Center height="44px">
                <Image src={logo} alt={'logo'} width={'30px'} height={'30px'} />
              </Center>
              <Center height="30" mx={'20px'}>
                <Divider orientation="vertical" borderColor={'#425570'} />
              </Center>
            </Hide>
            <Text
              fontWeight={700}
              fontSize={{ base: 16, md: 36 }}
              lineHeight={'36px'}
              color={'#11284b'}
              textTransform={'uppercase'}
            >
              Radon Management Cloud
            </Text>
          </HStack>
          <Show below="md">
            {isOpenDrawer ? (
              <CButton variant="ghost" color="11284b" p={0} onClick={onCloseDrawer}>
                <MdClose size={32} />
              </CButton>
            ) : (
              <CButton variant="ghost" color="11284b" p={0} onClick={onOpenDrawer}>
                <MdMenu size={32} />
              </CButton>
            )}
          </Show>
        </Flex>

        <Show below="md">
          <Box
            visibility={isOpenDrawer ? 'visible' : 'hidden'}
            opacity={isOpenDrawer ? 1 : 0}
            maxH={isOpenDrawer ? '100vh' : 0}
            transition={'all .5s ease-in-out'}
            position={'absolute'}
            width={'100%'}
            top={'136px'}
            zIndex={99}
            height={'100%'}
          >
            <VStack spacing={0} bgColor={'#fff'} px={2} boxShadow={'0px 5px 20px rgba(0,0,0,0.25)'}>
              <PopoverLink label="Dashboard" onClick={() => redirect('/dashboard', true)} />
              <PopoverLink label="Manage Tests" onClick={() => redirect('/tests', true)} />
              <PopoverLink label="My Profile" onClick={() => redirect('/profile', true)} />
              <If condition={isCompanyUser(userRole)}>
                <PopoverLink label="My Company Info" onClick={() => redirect('/mycompany', true)} />
              </If>
              <If condition={isSpruceAdmin(userRole)}>
                <PopoverLink label="Company Management" onClick={() => redirect('/companies', true)} />
              </If>
              <If condition={isAdmin(userRole)}>
                <PopoverLink label="User Management" onClick={() => redirect('/users', true)} />
                <PopoverLink label="Report Management" onClick={() => redirect('/reports', true)} />
                <PopoverLink label="Email Templates" onClick={() => redirect('/email', true)} />
              </If>
              <If condition={isDynamicLayoutAdmin(userRoles)}>
                <PopoverLink label="Layout Management" onClick={() => redirect('/layouts', true)} />
                <PopoverLink label="Report Default Values" onClick={() => redirect('/defaultValues', true)} />
              </If>
              <PopoverLink label="Help/Support" onClick={() => redirect('/help', true)} />
              <Divider />
              <LogoutLinkContainer />
            </VStack>
          </Box>
        </Show>

        <Hide below="md">
          <HStack>
            <MenuButton
              label="Dashboard"
              isActive={includes(pathname, 'dashboard')}
              onClick={() => redirect('/dashboard')}
            />
            <MenuButton
              label="Manage Tests"
              isActive={includes(pathname, 'tests')}
              onClick={() => redirect('/tests')}
            />

            <Popover>
              <PopoverTrigger>
                <CButton
                  px={2.5}
                  variant="ghost"
                  height={'44px'}
                  color={'#11284B'}
                  fontSize={'16px'}
                  _hover={{ bg: '#11284B', color: '#fff' }}
                  _active={{ bg: '#11284B', color: '#fff' }}
                  _focus={{ boxShadow: 'none ' }}
                  borderRadius={0}
                  onClick={onToggle}
                  onMouseEnter={onToggle}
                >
                  <MdSettings />
                  <Text ml={1} fontWeight={700}>
                    Settings
                  </Text>
                  <AiFillCaretDown />
                </CButton>
              </PopoverTrigger>
              <PopoverContent
                _focus={{ boxShadow: 'none ' }}
                _focusVisible={{ boxShadow: 'none ' }}
                _active={{ boxShadow: 'none ' }}
                _hover={{ boxShadow: 'none ' }}
              >
                <PopoverBody
                  textAlign={'left'}
                  p={0}
                  _focus={{ boxShadow: 'none ' }}
                  _focusVisible={{ boxShadow: 'none ' }}
                  _active={{ boxShadow: 'none ' }}
                  _hover={{ boxShadow: 'none ' }}
                >
                  <VStack spacing={0}>
                    <PopoverLink label="My Profile" onClick={() => redirect('/profile', true)} />
                    <If condition={isCompanyUser(userRole)}>
                      <PopoverLink label="My Company Info" onClick={() => redirect('/mycompany', true)} />
                    </If>
                    <If condition={isSpruceAdmin(userRole)}>
                      <PopoverLink label="Company Management" onClick={() => redirect('/companies', true)} />
                    </If>
                    <If condition={isAdmin(userRole)}>
                      <PopoverLink label="User Management" onClick={() => redirect('/users', true)} />
                      <PopoverLink label="Report Management" onClick={() => redirect('/reports', true)} />
                      <PopoverLink label="Email Templates" onClick={() => redirect('/email', true)} />
                    </If>
                    <If condition={isDynamicLayoutAdmin(userRoles)}>
                      <PopoverLink label="Layout Management" onClick={() => redirect('/layouts', true)} />
                      <PopoverLink label="Report Default Values" onClick={() => redirect('/defaultValues', true)} />
                    </If>
                    <PopoverLink label="Help/Support" onClick={() => redirect('/help', true)} />
                    <Divider />
                    <LogoutLinkContainer />
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </HStack>
        </Hide>
      </Stack>
    </Container>
  );
};

AuthenticatedLayout.propTypes = {
  userRole: PropTypes.string,
  userRoles: PropTypes.array,
};

export default AuthenticatedLayout;



// WEBPACK FOOTER //
// src/components/layout/AuthenticatedLayout/AuthenticatedLayout.jsx